import React from 'react'
import SearchFilterIcon from '../../assets/icons/search-filter'
import CustomTable from '../../components/Table'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableRow from '../../components/Table/TableRow'
import CustomTableData from '../../components/Table/TableData'
import SearchIcon from '../../assets/icons/search.svg'
import Modal from '../../components/Modal/Security/ConfirmGuestModal'
import { Result } from '../../interfaces/security'
import { showToast } from '../../utils/toast'
import { removeUnderscore } from '../../utils/removeUnderscore'
import { formatDate } from '../../utils/formatDate'
import { ViewStaffsSkeletonLoader } from '../../components/Loader/ViewStaffsSkeletonLoader'
import TablePagination from '../../components/Table/Pagination'
import { getStatusTextClass } from '../../utils/getStatusColor'
import SecurityService from '../../api/security'
import Empty from '../../assets/icons/Empty'
import { useDebounce } from '../../hooks/useDebounce'

const securityStatuses = [
  { label: 'All', value: 'All' },
  { label: 'Valid', value: 'valid' },
  { label: 'Expired', value: 'expired' },
  { label: 'Used', value: 'used' },
]

const tableHeadData = [
  'Name/Email of Residents',
  'Name of guest',
  'Date',
  'Address',
  'Status',
  '',
]

interface ViewSecurityProps {
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
}

const ViewSecurity = ({ handleViewSelect }: ViewSecurityProps) => {
  const [statusFilter, setStatusFilter] = React.useState({
    label: 'All',
    value: 'All',
  })

  const [searchTerm, setSearchTerm] = React.useState('')
  const [currentPage, setCurrentPage] = React.useState(1)
  const debouncedSearchTerm = useDebounce(searchTerm, 500)

  const { data, isLoading, mutate } = SecurityService.useGetGuestList({
    page: currentPage,
    limit: 10,
    search: debouncedSearchTerm,
    code: debouncedSearchTerm,
    status: statusFilter.value,
  })

  const [openModal, setOpenModal] = React.useState(false)
  const [selectedGuest, setSelectedGuest] = React.useState<Result | null>(null)
  const [isConfirmingEntry, setIsConfirmingEntry] = React.useState(false)

  const handleStatusSelect = (status: { value: string; label: string }) => {
    setStatusFilter(status)
    setCurrentPage(1)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1)
  }

  const handleRowClick = (guest: Result) => {
    setSelectedGuest(guest)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedGuest(null)
  }

  const handleConfirmGuestEntry = async () => {
    if (!selectedGuest || isConfirmingEntry) return

    setIsConfirmingEntry(true)
    try {
      const entryData = {
        name: selectedGuest.name,
        code: selectedGuest.code ?? 'N/A',
        destination: selectedGuest.property.address ?? 'N/A',
        date: formatDate(selectedGuest.startsAt),
        validity: formatDate(selectedGuest.expiresAt),
        codeType: removeUnderscore(selectedGuest.type),
      }

      const response = await SecurityService.confirmGuestEntry(
        selectedGuest.code,
        entryData
      )

      await mutate((prev: any) => {
        if (!prev?.results) return prev

        return {
          ...prev,
          results: prev.results.map((guest: Result) =>
            guest.id === selectedGuest.id ? { ...guest, status: 'used' } : guest
          ),
        }
      }, false)

      await mutate()

      showToast({
        status: 'success',
        title: response?.message || 'Guest entry confirmed successfully.',
      })

      handleCloseModal()
    } catch (error: any) {
      showToast({
        status: 'error',
        title:
          error?.response?.data?.message || error?.message || 'Error confirming guest.',
      })
    } finally {
      setIsConfirmingEntry(false)
    }
  }

  return (
    <div className="mt-8 grid gap-y-6">
      <form
        onSubmit={(e) => e.preventDefault()}
        className="search-filter-bar flex w-full justify-between gap-2.5"
      >
        <div className="w-full flex bg-gray-100 rounded-lg items-center px-2">
          <div className="relative flex items-center w-full">
            <img src={SearchIcon} alt="Search Icon" />
            <input
              placeholder="Search"
              className="py-2.5 w-full bg-transparent h-full"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <div className="w-fit p-2.5 gap-2 whitespace-nowrap relative flex items-center group justify-end font-semibold">
          <p>{statusFilter.label}</p>
          <div className="bg-gray-100 w-8 h-8 flex items-center justify-center rounded-lg">
            <SearchFilterIcon />
          </div>
          <div className="absolute z-10 top-12 right-0 invisible group-hover:visible cursor-pointer w-40 rounded-xl overflow-hidden shadow-[4px_4px_20px_4px_rgba(33,37,41,0.10)] border bg-white">
            {securityStatuses.map((status) => (
              <p
                onClick={() => handleStatusSelect(status)}
                className={`hover:bg-gray-200 p-3 ${
                  status.value === statusFilter.value ? 'text-[#069E2D]' : ''
                } border-t`}
                key={status.value}
              >
                {status.label}
              </p>
            ))}
          </div>
        </div>
      </form>

      {isLoading ? (
        <ViewStaffsSkeletonLoader />
      ) : data?.results?.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[300px] w-full gap-4">
          <Empty />
          <p className="text-sm font-semibold">No results found</p>
        </div>
      ) : (
        <>
          <CustomTable>
            <CustomTableHead dataList={tableHeadData} />
            <CustomTableBody loading={isLoading}>
              {data?.results.map((guest) => (
                <CustomTableRow
                  key={guest.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleRowClick(guest)}
                >
                  <CustomTableData dataLabel="Name/Email of resident" text="">
                    <div>
                      <div>{`${guest.host.firstName} ${guest.host.lastName}`}</div>
                      <div>{guest.host.email || 'N/A'}</div>
                    </div>
                  </CustomTableData>

                  <CustomTableData dataLabel="Name of guest" text={guest.name} />

                  <CustomTableData dataLabel="Date" text={formatDate(guest.startsAt)} />

                  <CustomTableData
                    dataLabel="Address"
                    text={guest.property?.address || 'N/A'}
                  />

                  <CustomTableData dataLabel="Status" text="">
                    <p className={getStatusTextClass(guest.status)}>
                      {guest.status.charAt(0).toUpperCase() + guest.status.slice(1)}
                    </p>
                  </CustomTableData>

                  <CustomTableData dataLabel="More" text="..." />
                </CustomTableRow>
              ))}
            </CustomTableBody>
          </CustomTable>

          {data?.results && data.results.length > 0 && (
            <TablePagination
              totalPages={data.totalPages}
              currPage={data.page}
              setCurrentPage={setCurrentPage}
            />
          )}
        </>
      )}

      {selectedGuest && (
        <Modal
          isOpen={openModal}
          onClose={handleCloseModal}
          onConfirm={handleConfirmGuestEntry}
          isConfirmDisabled={['used', 'expired'].includes(selectedGuest.status)}
          isConfirming={isConfirmingEntry}
          guest={{
            name: selectedGuest.name,
            code: selectedGuest.code ?? 'N/A',
            destination: selectedGuest.property.address ?? 'N/A',
            date: formatDate(selectedGuest.startsAt),
            validity: formatDate(selectedGuest.expiresAt),
            codeType: removeUnderscore(selectedGuest.type),
          }}
        />
      )}
     
    </div>
  )
}

export default ViewSecurity
